import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Paper
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { isUserSuperAdminSelector } from 'app/selectors/userSelectors';
import { usePersonaGetPersonaDeletionValidation } from '@/api-gen/api';
import type { PersonaDeletionValidationDto, PersonaUsageScenarioDto } from '@/api-gen/api.schemas';
import AppRoutes from 'app/utils/routes';
import type { I18nPersonasNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import { useTranslation } from 'react-i18next';

interface PersonaScenariosDisplayProps {
  personaId?: number;
}

const PersonaScenariosDisplay: React.FC<PersonaScenariosDisplayProps> = ({
  personaId
}) => {
  const { t: translate } = useTranslation(I18nNamespace.Personas);
  const { t: translateCommon } = useTranslation(I18nNamespace.Common);
  const isSuperAdmin = useSelector(isUserSuperAdminSelector);

  // Use the same API hook as DeletePersonaDialog
  const { 
    data: apiResponse, 
    isLoading: loading, 
    error,
    refetch 
  } = usePersonaGetPersonaDeletionValidation<PersonaDeletionValidationDto, Error>(personaId!, {
    query: {
      enabled: !!personaId,
      refetchOnMount: 'always',
      staleTime: 0
    }
  });

  // Extract validation data from the API response
  const validation = ((apiResponse as any)?.data || apiResponse) as PersonaDeletionValidationDto | undefined;

  const groupedByOrg = useMemo(() => {
    if (!validation?.scenariosUsingPersona || !Array.isArray(validation.scenariosUsingPersona)) {
      return {};
    }
    
    if (!isSuperAdmin) {
      return { _: validation.scenariosUsingPersona };
    }
    
    return validation.scenariosUsingPersona.reduce<Record<string, PersonaUsageScenarioDto[]>>((acc, s) => {
      const key = s.organizationName ?? '—';
      if (!acc[key]) acc[key] = [];
      acc[key].push(s);
      return acc;
    }, {});
  }, [validation, isSuperAdmin]);

  const scenarioEditorUrl = (id?: number) => id ? AppRoutes.SceneWithId.replace(':id', String(id)) : '';
  const hasScenarios = Object.keys(groupedByOrg).length > 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error?.message || 'Failed to load scenarios'}
      </Alert>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          <AssignmentIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
          {translate(nameof.full<I18nPersonasNs>((n) => n.deleteDialog.validation.scenariosUsingPersona))}
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon fontSize="small" />}
          onClick={() => refetch()}
          disabled={loading}
          sx={{ minWidth: 'auto' }}
        >
          {translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.refresh))}
        </Button>
      </Box>

      {!hasScenarios ? (
        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {translate(nameof.full<I18nPersonasNs>((n) => n.deleteDialog.validation.noUsage))}
          </Typography>
        </Box>
      ) : (
        <Paper 
          variant="outlined"
          sx={{ 
            maxHeight: 300, 
            overflowY: 'auto', 
            backgroundColor: 'background.paper'
          }}
        >
          {Object.entries(groupedByOrg).map(([org, items], idx, arr) => (
            <Box key={org}>
              {isSuperAdmin && Object.keys(groupedByOrg).length > 1 && (
                <Box 
                  sx={{ 
                    px: 2, 
                    py: 1.5, 
                    backgroundColor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    fontWeight={600}
                    color="primary.main"
                  >
                    {org}
                  </Typography>
                </Box>
              )}
              <List dense disablePadding>
                {(items as PersonaUsageScenarioDto[]).map((s, itemIdx) => (
                  <ListItem
                    key={s.scenarioId}
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      },
                      borderBottom: itemIdx < (items as PersonaUsageScenarioDto[]).length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}
                  >
                    <ListItemText
                      primary={s.scenarioName}
                      secondary={isSuperAdmin && Object.keys(groupedByOrg).length === 1 ? s.organizationName : undefined}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                    <Button
                      size="small"
                      href={scenarioEditorUrl(s.scenarioId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<OpenInNewIcon fontSize="small" />}
                      sx={{ ml: 1, minWidth: 'auto', flexShrink: 0 }}
                    >
                      {translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.open))}
                    </Button>
                  </ListItem>
                ))}
              </List>
              {idx < arr.length - 1 && (
                <Divider sx={{ borderColor: 'primary.main', borderWidth: 1 }} />
              )}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default PersonaScenariosDisplay;
